import { useEffect, useMemo, useState } from 'react'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import { useWorkspace } from '../../context/useWorkspace'
import { useAuth } from '../../context/useAuth'
import { useTimeline } from '../../context/useTimeline'
import { useNls } from '../../nls/nlsProvider'
import { useToast } from '../../context/useToast'
import { Button } from '../Common'
import { isTimesheetsTierEligible } from '../../services/featureRegistry'
import { switchedOffModulesFor } from '../../utils/workspaceModules'
import {
  stopClock,
  subscribeMyClockState,
} from '../../services/timesheetsService'
import type { ClockState } from '../../types/timesheets'
import './timesheets.css'

export interface RunningTimerBarProps {
  className?: string
  overrideClockState?: ClockState | null
}

export const RunningTimerBar = ({
  className = '',
  overrideClockState,
}: RunningTimerBarProps) => {
  const { activeWorkspaceId, workspaces } = useWorkspace()
  const { user } = useAuth()
  const { t } = useNls()
  const { showToast } = useToast()
  const { elements, checklistItems } = useTimeline()

  const workspace = workspaces.find((w) => w.id === activeWorkspaceId) ?? null

  const isModuleOff = useMemo(() => {
    if (!workspace) return true
    return switchedOffModulesFor(workspace).has('timesheets')
  }, [workspace])

  const isTierLocked = useMemo(() => {
    if (!workspace) return true
    return !isTimesheetsTierEligible(workspace.plan, workspace)
  }, [workspace])

  const [clockState, setClockState] = useState<ClockState | null>(null)
  const [stopping, setStopping] = useState(false)
  const [now, setNow] = useState<number>(() => Date.now())

  const effectiveState = overrideClockState !== undefined ? overrideClockState : clockState

  useEffect(() => {
    if (overrideClockState !== undefined) return
    if (!workspace?.id || !user?.uid || isModuleOff || isTierLocked) {
      return
    }

    const unsubscribe = subscribeMyClockState(
      workspace.id,
      user.uid,
      (state) => {
        setClockState(state)
      },
      (err) => {
        console.error('[RunningTimerBar] subscribeMyClockState error:', err)
      },
    )

    return () => {
      unsubscribe?.()
      setClockState(null)
    }
  }, [workspace?.id, user?.uid, isModuleOff, isTierLocked, overrideClockState])

  useEffect(() => {
    if (!effectiveState?.startedAt) return
    const interval = setInterval(() => {
      setNow(Date.now())
    }, 1000)
    return () => clearInterval(interval)
  }, [effectiveState?.startedAt])

  const nodeTitle = useMemo(() => {
    if (!effectiveState?.nodeId) {
      return t('NLSTimesheetsTimerBarClockedIn')
    }
    const matchedEl = elements?.find((e) => e.id === effectiveState.nodeId)
    if (matchedEl?.title) return matchedEl.title
    const matchedItem = checklistItems?.find((c) => c.id === effectiveState.nodeId) as { id: string; title?: string; text?: string } | undefined
    if (matchedItem?.title || matchedItem?.text) return matchedItem.title || matchedItem.text!
    return effectiveState.nodeId
  }, [effectiveState, elements, checklistItems, t])

  const elapsedText = useMemo(() => {
    if (!effectiveState?.startedAt) return '00:00'
    const startMs = new Date(effectiveState.startedAt).getTime()
    if (isNaN(startMs)) return '00:00'
    const totalSeconds = Math.max(0, Math.floor((now - startMs) / 1000))
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }, [effectiveState, now])

  if (isModuleOff || isTierLocked || !effectiveState) {
    return null
  }

  const handleStop = async () => {
    if (!workspace?.id || stopping) return
    setStopping(true)
    try {
      const res = await stopClock({ workspaceId: workspace.id })
      if (res.success) {
        showToast(t('NLSTimesheetsEntrySavedToast'), 'success')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not stop timer'
      showToast(msg, 'error')
    } finally {
      setStopping(false)
    }
  }

  return (
    <div
      className={`runningTimerBar ${className}`.trim()}
      role="timer"
      aria-label={`${nodeTitle}: ${elapsedText}`}
      data-testid="running-timer-bar"
    >
      <span className="runningTimerBarDot" aria-hidden="true" />
      <span className="runningTimerBarNode" title={nodeTitle}>
        {nodeTitle}
      </span>
      <span className="runningTimerBarElapsed" data-testid="running-timer-elapsed">
        {elapsedText}
      </span>
      <Button
        variant="primary"
        size="sm"
        className="runningTimerBarStopBtn"
        onClick={handleStop}
        disabled={stopping}
        data-testid="running-timer-stop-btn"
        aria-label={t('NLSTimesheetsTimerBarStop')}
      >
        <StopRoundedIcon sx={{ fontSize: 14 }} aria-hidden="true" />
        <span className="runningTimerBarStopLabel">{t('NLSTimesheetsTimerBarStop')}</span>
      </Button>
    </div>
  )
}
